import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { AccessibleSystemNavigator } from "./components/a11y/AccessibleSystemNavigator";
import { PortalTransition } from "./components/PortalTransition";
import { MobileDock } from "./components/MobileDock";
import { InstrumentReadout } from "./components/InstrumentReadout";
import { ExperienceDirector } from "./components/ExperienceDirector";
import { DemoTourDirector } from "./components/DemoTourDirector";
import { CinematicFocusTransition } from "./components/CinematicFocusTransition";
import { CornerTelemetry } from "./components/CornerTelemetry";
import { InspectorPanel } from "./components/InspectorPanel";
import { IntroVeil } from "./components/IntroVeil";
import { TimelineRail } from "./components/TimelineRail";
import { TopBar } from "./components/TopBar";
import { HeroStage } from "./components/HeroStage";
import { getCatalog } from "./i18n";
import { loadSnapshot } from "./lib/meshApi";
import { focusedNodeId, useNexusStore } from "./store/nexusStore";
import { panelSpring } from "./lib/motion";
import { NexusSceneHost } from "./engine/NexusSceneHost";

const CommandPalette = lazy(() =>
  import("./components/CommandPalette").then((module) => ({ default: module.CommandPalette })),
);

const commonRefreshRates = [30, 48, 50, 60, 72, 75, 90, 100, 120, 144, 165, 180, 200, 240, 360] as const;

function nearestRefreshRate(frameIntervals: number[]) {
  const sorted = [...frameIntervals].sort((left, right) => left - right);
  const fastStableInterval = sorted[Math.floor(sorted.length * 0.1)] ?? 1000 / 60;
  const measured = 1000 / Math.max(fastStableInterval, 1);
  return commonRefreshRates.reduce((nearest, candidate) =>
    Math.abs(candidate - measured) < Math.abs(nearest - measured) ? candidate : nearest,
  );
}

export default function App() {
  const [displayRefreshHz, setDisplayRefreshHz] = useState(60);
  const hydrateSnapshot = useNexusStore((state) => state.hydrateSnapshot);
  const focusCosmos = useNexusStore((state) => state.focusCosmos);
  const setCommandOpen = useNexusStore((state) => state.setCommandOpen);
  const toggleChrome = useNexusStore((state) => state.toggleChrome);
  const chromeVisible = useNexusStore((state) => state.chromeVisible);
  const idleMode = useNexusStore((state) => state.idleMode);
  const easterEgg = useNexusStore((state) => state.easterEgg);
  const accessibleMode = useNexusStore((state) => state.accessibleMode);
  const heroVisible = useNexusStore((state) => state.heroVisible);
  const commandOpen = useNexusStore((state) => state.commandOpen);
  const demoTourActive = useNexusStore((state) => state.demoTourActive);
  const setDemoTourActive = useNexusStore((state) => state.setDemoTourActive);
  const focus = useNexusStore((state) => state.focus);
  const snapshot = useNexusStore((state) => state.snapshot);
  const locale = useNexusStore((state) => state.locale);
  const ui = getCatalog(locale).ui;
  const focusedId = focusedNodeId(focus);
  const blackHoleFocus = focusedId !== null && snapshot?.projection.nodes[focusedId]?.archetype === "black-hole";

  useEffect(() => {
    const controller = new AbortController();
    void loadSnapshot(controller.signal)
      .then((result) => hydrateSnapshot(result.data, result.source, result.error))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) console.error("Snapshot initialization failed", error);
      });
    return () => controller.abort();
  }, [hydrateSnapshot]);

  useEffect(() => {
    let refreshFrame = 0;
    let previousFrame = 0;
    const frameIntervals: number[] = [];
    const sampleRefreshRate = (time: number) => {
      if (previousFrame) frameIntervals.push(time - previousFrame);
      previousFrame = time;
      if (frameIntervals.length < 24) {
        refreshFrame = requestAnimationFrame(sampleRefreshRate);
        return;
      }
      setDisplayRefreshHz(nearestRefreshRate(frameIntervals));
    };
    refreshFrame = requestAnimationFrame(sampleRefreshRate);
    return () => {
      cancelAnimationFrame(refreshFrame);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCommandOpen(false);
        setDemoTourActive(false);
        focusCosmos();
      }
      if (event.key.toLowerCase() === "h" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const target = event.target as HTMLElement | null;
        if (target?.matches("input, textarea, [contenteditable='true']")) return;
        toggleChrome();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [focusCosmos, setCommandOpen, setDemoTourActive, toggleChrome]);

  return (
    <MotionConfig reducedMotion="user">
      <main
        className={`nexus-shell${blackHoleFocus ? " is-black-hole-focus" : ""}${idleMode ? " is-idle" : ""}${easterEgg ? " is-event-horizon" : ""}${accessibleMode ? " is-accessible-observatory" : ""}${heroVisible ? " is-hero-visible" : ""}${demoTourActive ? " is-demo-tour" : ""}`}
      >
        <div className="observatory-optics" aria-hidden="true" />
        <NexusSceneHost displayRefreshHz={displayRefreshHz} />
        <div className="hero-legibility-scrim" aria-hidden="true" />
        <CinematicFocusTransition />
        <AccessibleSystemNavigator />
        <ExperienceDirector />
        <DemoTourDirector />

        <AnimatePresence>
          {chromeVisible && (
            <motion.div
              className="ui-layer"
              initial={{ opacity: 0, scale: 0.998, filter: "blur(3px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.998, filter: "blur(2px)" }}
              transition={panelSpring}
            >
              <TopBar />
              <HeroStage />
              <CornerTelemetry />
              <InstrumentReadout />
              <InspectorPanel />
              <TimelineRail />
              <div className="interaction-hint" aria-hidden="true">
                <span>{ui.click}</span> {ui.focus} <i /> <span>{ui.escape}</span> {ui.cosmos} <i /> <span>H</span>{" "}
                {ui.hideUi}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <MobileDock />
        {commandOpen && (
          <Suspense fallback={null}>
            <CommandPalette />
          </Suspense>
        )}
        <PortalTransition />
        <IntroVeil />
      </main>
    </MotionConfig>
  );
}
